import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { Box, Dialog, DialogActions, DialogContent, Divider, Grid, Skeleton, Typography } from '@mui/material';
import React, { useEffect } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useRecoilValue } from 'recoil';
import { AutomatedNotificationService } from '../../../Component/Api/AutomatedNotifications/AutomatedNotificationService';
import { StaticDataService } from '../../../Component/Api/StaticData/StaticDataService';
import { UserService } from '../../../Component/Api/User/UserService';
import AppMultiSelect from '../../../Component/Common/Components/FormValidationInputs/AppMultiSelect';
import AppSelectList, { IDropDownOptions } from '../../../Component/Common/Components/FormValidationInputs/AppSelectList';
import AppTextInput from '../../../Component/Common/Components/FormValidationInputs/AppTextInput';
import {
    ClientStatusList,
    NotificationTriggerListForPlanner,
    NotificationTriggerListForApplicationTracker,
    VerificationProcessStatusList,
} from '../../../Component/Common/Constants/Lists/DropdownListOptions';
import { AutomatedNotificationRuleRequest, AutomatedNotificationRuleResponse } from '../../../Component/Model/Dto/AutomatedNotification/AutomatedNotificationRule';
import { AutomatedNotificationTypeAvailableStatus } from '../../../Component/Model/Dto/StaticData';
import { UserAtom } from '../../../State/user.Atom';
import { CustomDialogTitle } from '../../Common/Custom/CustomDialogTitle';
import { AutomatedNotificationValidationSchema } from './AutomatedNotificationValidationSchema';
import { AutomatedNotificationType, NotificationTriggerType } from "../../../Component/Model/Enum/BEE123Enums";
import { VerificationProcessService } from '../../../Component/Api/VerificationAgency/VerificationProcess/VerificationProcessService';
import { MDButton } from '../../../Component/Common/Components/MDButton';
import { VerificationClientStatus } from '../../Common/StaticData/VAClientStatus';
import { SkeletonLoading } from '../../../Component/Common/SkeletonLoading/SkeletonLoading';
import { ApplicationStatusOptionsDto } from '../../../Component/Model/Dto/VerificationAgency/VerificationAgencyClientApplication';
import { VerificationAgencyClientApplicationService } from '../../../Component/Api/VerificationAgency/VerificationAgencyClientApplication/VerificationAgencyClientApplicationService';
import { objectToDropdownOptions } from '../../../Helpers/objectHelper';

interface IProps {
    id: number;
    showModal: boolean;
    handleClose: () => void;
}

export const AutomatedNotificationModal = (props: IProps) => {
    const [isLoading, setIsLoading] = React.useState(false);
    const [automatedNotificationTypes, setAutomatedNotificationTypes] = React.useState<IDropDownOptions[]>([]);
    const [steps, setSteps] = React.useState<IDropDownOptions[]>([]);
    const [status, setStatus] = React.useState<IDropDownOptions[]>([]);
    const [emailUsers, setEmailUsers] = React.useState<IDropDownOptions[]>([]);
    const [processTypes, setProcessTypes] = React.useState<IDropDownOptions[]>([]);
    const [automatedNotificationTypeAvailableStatuses, setAutomatedNotificationTypeAvailableStatuses] = React.useState<AutomatedNotificationTypeAvailableStatus[]>([]);
    const [notificationTrigger, setNotificationTrigger] = React.useState<IDropDownOptions[]>([]);
    const [options, setOptions] = React.useState<ApplicationStatusOptionsDto>();
    const [verificationProgressOptionsList, setVerificationProgressOptionsList] = React.useState<IDropDownOptions[]>([]);
    const [enquiryStatusOptionsList, setEnquiryStatusOptionsList] = React.useState<IDropDownOptions[]>([]);
    const [applicationStatusOptionsList, setApplicationStatusOptionsList] = React.useState<IDropDownOptions[]>([]);
    const [proposalAgreementStatusOptionsList, setProposalAgreementStatusOptionsList] = React.useState<IDropDownOptions[]>([]);
    const [invoiceStatusOptionsList, setInvoiceStatusOptionsList] = React.useState<IDropDownOptions[]>([]);
    const [wrapUpOptionsList, setWrapUpOptionsList] = React.useState<IDropDownOptions[]>([]);
    const [defaultOptionsList, setDefaultOptionsList] = React.useState<IDropDownOptions[]>([]);
    const [verificationAgreementOptionsList, setVerificationAgreementOptionsList] = React.useState<IDropDownOptions[]>([]);
    const [stepId, setStepId] = React.useState<number>();

    const user = useRecoilValue(UserAtom);

    const { control, watch, handleSubmit, formState: { isSubmitting }, reset, setValue, trigger } = useForm({
        mode: 'all',
        resolver: yupResolver(AutomatedNotificationValidationSchema)
    });

    const numberOfDaysBefore = watch("numberOfDaysToSendBeforeScheduleDate");
    const numberOfDaysAfter = watch("numberOfDaysToSendAfterScheduleDate");
    const daysAfterDisabled = Boolean(numberOfDaysBefore);
    const daysBeforeDisabled = Boolean(numberOfDaysAfter);

    const automatedNotificationType = watch("automatedNotificationTypeId");
    const notificationTriggerType = watch("notificationTrigger");
    const stepType = watch("stepToAction");

    useEffect(() => {
        trigger("numberOfDaysToSendBeforeScheduleDate");
        trigger("numberOfDaysToSendAfterScheduleDate");
    }, [numberOfDaysBefore, numberOfDaysAfter, notificationTrigger, automatedNotificationType]);

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const notificationTypesPromise = StaticDataService.getAutomatedNotificationTypes();
            const availableStatusPromise = StaticDataService.getAutomatedNotificationTypeAvailableStatuses();
            const processTypesPromise = VerificationProcessService.GetVerificationProcessTypes();
            const usersPromise = UserService.getNonClientUsers();
            const dropdownOptionsPromise = VerificationAgencyClientApplicationService.GetDropdownOptions();

            const [notificationTypesResponse, availableStatusResponse, processTypesResponse, usersResponse, enumsResponse] = await Promise.all([
                notificationTypesPromise,
                availableStatusPromise,
                processTypesPromise,
                usersPromise,
                dropdownOptionsPromise
            ]);

            const users = usersResponse.data.map(({ id, name }) => ({ id: id, value: name }));
            const mappedTypes = processTypesResponse.data.map(item => ({
                id: item.id,
                value: item.description
            }));

            setAutomatedNotificationTypes(notificationTypesResponse.data);
            setAutomatedNotificationTypeAvailableStatuses(availableStatusResponse.data);
            setEmailUsers(users);
            setProcessTypes(mappedTypes);
            setSteps(mappedTypes);
            setOptions(enumsResponse.data);

            await fetchNotificationData();
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    }

    const setStatusOptions = (() => {
        if (options) {
            const verificationProgressOptions = objectToDropdownOptions(options.verificationProgressStatuses);
            const enquiryStatusOptions = objectToDropdownOptions(options.enquiryStatuses);
            const applicationStatusOptions = objectToDropdownOptions(options.applicationStatuses);
            const proposalAgreementStatusOptions = objectToDropdownOptions(options.proposalStatuses);
            const invoiceStatusOptions = objectToDropdownOptions(options.invoiceStatuses);
            const wrapUpStatusOptions = objectToDropdownOptions(options.wrapUpStatuses);
            const defaultStatusOptions = objectToDropdownOptions(options.defaultStatuses);
            const verificationAgreementOptions = objectToDropdownOptions(options.verificationAgreementStatuses);

            setVerificationProgressOptionsList(verificationProgressOptions);
            setEnquiryStatusOptionsList(enquiryStatusOptions);
            setApplicationStatusOptionsList(applicationStatusOptions);
            setProposalAgreementStatusOptionsList(proposalAgreementStatusOptions);
            setInvoiceStatusOptionsList(invoiceStatusOptions);
            setWrapUpOptionsList(wrapUpStatusOptions);
            setDefaultOptionsList(defaultStatusOptions);
            setVerificationAgreementOptionsList(verificationAgreementOptions);
        }
    });

    useEffect(() => {
        fetchData();
    }, [props.id]);

    useEffect(() => {
        setStatusOptions();
    }, [options])

    useEffect(() => {
        if (
            verificationProgressOptionsList.length &&
            enquiryStatusOptionsList.length &&
            applicationStatusOptionsList.length &&
            proposalAgreementStatusOptionsList.length &&
            invoiceStatusOptionsList.length &&
            wrapUpOptionsList.length &&
            defaultOptionsList.length &&
            verificationAgreementOptionsList.length &&
            stepId
        ) {
            getStatuses(stepId);
        }
    }, [
        verificationProgressOptionsList,
        enquiryStatusOptionsList,
        applicationStatusOptionsList,
        proposalAgreementStatusOptionsList,
        invoiceStatusOptionsList,
        wrapUpOptionsList,
        defaultOptionsList,
        verificationAgreementOptionsList,
        stepId
    ]);

    const resetData = (data: AutomatedNotificationRuleResponse) => {
        getKeys(data.automatedNotificationTypeId);
        reset(data);
    }

    const fetchNotificationData = async () => {
        setIsLoading(true);
        try {
            if (props.id > 0) {
                const response = await AutomatedNotificationService.GetById(props.id);
                resetData(response.data);
                if (response.data.automatedNotificationTypeId == AutomatedNotificationType.Scorecard_Planner_And_Tracker) {
                    if (response.data.statusId) {
                        setValue('notificationTrigger', NotificationTriggerType.StatusChangePlanner);
                        setValue('statusToAction', response.data.statusId);
                    } else {
                        setValue('notificationTrigger', NotificationTriggerType.ScheduledDate);
                        setValue('numberOfDaysToSendBeforeScheduleDate', response.data.numberOfDaysToSendBeforeScheduleDate)
                        setValue('numberOfDaysToSendAfterScheduleDate', response.data.numberOfDaysToSendAfterScheduleDate)
                    }
                } else {
                    if (response.data.statusId && !response.data.daysElapsed) {
                        setValue('statusToAction', response.data.statusId);
                        setValue('notificationTrigger', NotificationTriggerType.StatusChangeApplicationTracker);
                    }
                    else {
                        setValue('daysElapsed', response.data.daysElapsed);
                        setValue('statusToAction', response.data.statusId);
                        setValue('notificationTrigger', NotificationTriggerType.DaysElapsed);
                    }
                    setStepId(response.data.stepId);
                }
                setValue('stepToAction', response.data.stepId);
            }
        } catch (error) {
        }
        finally {
            setIsLoading(false);
        }
    }

    const handleDataSubmit = async (data: FieldValues) => {

        try {
            var newRuleRequestData: AutomatedNotificationRuleRequest = {
                id: data.id,
                automatedNotificationTypeId: data.automatedNotificationTypeId,
                message: data.message,
                addedByUserId: user.username,
                numberOfDaysToSendAfterScheduleDate: notificationTriggerType == NotificationTriggerType.ScheduledDate ? data.numberOfDaysToSendAfterScheduleDate : null,
                numberOfDaysToSendBeforeScheduleDate: notificationTriggerType == NotificationTriggerType.ScheduledDate ? data.numberOfDaysToSendBeforeScheduleDate : null,
                usersToNotify: data.usersToNotify,
                additionalUsersToNotify: data.additionalUsersToNotify,
                stepId: data.stepToAction,
                statusId: notificationTriggerType == NotificationTriggerType.ScheduledDate ? null : data.statusToAction,
                enabled: true,
                daysElapsed: notificationTriggerType == NotificationTriggerType.DaysElapsed ? data.daysElapsed : null,
            }

            if (props.id > 0) {
                const response = await AutomatedNotificationService.UpdateNewAutomatedNotificationRule(newRuleRequestData);
                resetData(response.data);
                toast.success("Notification rule updated");
            } else {
                const response = await AutomatedNotificationService.CreateNewAutomatedNotificationRule(newRuleRequestData);
                toast.success("New notification rule added");

            }
        } catch (error) {
            toast.error("Something went wrong. Notification not set");
        } finally {
            props.handleClose();
        }
    }

    const getKeys = (e: number) => {
        if (e == AutomatedNotificationType.Application_Status_Tracker) {
            setSteps(ClientStatusList);
            setNotificationTrigger(NotificationTriggerListForApplicationTracker);
        } else if (e == AutomatedNotificationType.Scorecard_Planner_And_Tracker) {
            if (steps.length > 0) {
                setSteps(processTypes);
            }
            setStatus(VerificationProcessStatusList);
            setNotificationTrigger(NotificationTriggerListForPlanner);
        }
        setValue("stepToAction", null);
        setValue("statusToAction", null);
    }

    const getStatuses = (e: number) => {
        switch (e) {
            case VerificationClientStatus.Application:
                setStatus(applicationStatusOptionsList);
                break;
            case VerificationClientStatus.Proposal:
                setStatus(proposalAgreementStatusOptionsList);
                break;
            case VerificationClientStatus.Verification_Agreement:
                setStatus(verificationAgreementOptionsList);
                break;
            case VerificationClientStatus.Verification_Progress:
                setStatus(verificationProgressOptionsList);
                break;
            case VerificationClientStatus.Invoice:
                setStatus(invoiceStatusOptionsList);
                break;
            case VerificationClientStatus.Risk_Assessment:
                setStatus(defaultOptionsList);
                break;
            case VerificationClientStatus.Wrap_Up:
                setStatus(wrapUpOptionsList);
                break;
            default:
                setStatus(enquiryStatusOptionsList);
                break;
        }
    }

    return (
        <Dialog open={props.showModal} onClose={props.handleClose} aria-labelledby="form-dialog-title" maxWidth='lg'>
            <CustomDialogTitle id="customized-dialog-title" onClose={props.handleClose}>
                {props.id > 0 ? "Edit" : "Create"} Automated Notification
            </CustomDialogTitle>
            <DialogContent>

                {isLoading ?
                    <Grid container spacing={10} width="800px">
                        <Grid item xs={12}>
                            <SkeletonLoading formLoading FieldAmount={2} height={60} width={"100%"} />
                            <Box mt={2}>
                                <Skeleton animation="wave" variant="rectangular" height={80} />
                            </Box>
                        </Grid>
                    </Grid>
                    :
                    <Grid container spacing={10} component="form" id='CompanyDetailsForm' onSubmit={handleSubmit(handleDataSubmit)} width="800px">
                        <Grid item xs={12}>
                            <Box mt={2}>
                                <AppSelectList onChange={getKeys} control={control} name="automatedNotificationTypeId" label="Notification Type" items={automatedNotificationTypes} size={"small"} />
                            </Box>
                            {automatedNotificationType && steps.length > 0 &&
                                <Box mt={2}>
                                    <AppSelectList
                                        onChange={(value) => {
                                            if (automatedNotificationType == AutomatedNotificationType.Application_Status_Tracker) {
                                                getStatuses(value);
                                            }
                                            setValue('statusToAction', null);
                                        }}
                                        control={control}
                                        name='stepToAction'
                                        label='Step'
                                        items={steps}
                                    />
                                </Box>
                            }
                            {((automatedNotificationType == AutomatedNotificationType.Scorecard_Planner_And_Tracker && stepType) || (automatedNotificationType == AutomatedNotificationType.Application_Status_Tracker && stepType)) &&
                                <Box mt={2}>
                                    <AppSelectList
                                        onChange={() => {
                                            setValue('statusToAction', null);
                                        }}
                                        control={control}
                                        name='notificationTrigger'
                                        label='Notification Trigger'
                                        items={notificationTrigger} />
                                </Box>
                            }
                            {((notificationTriggerType == NotificationTriggerType.StatusChangePlanner) || (notificationTriggerType == NotificationTriggerType.StatusChangeApplicationTracker)) &&
                                <Box mt={2}>
                                    <AppSelectList control={control} name='statusToAction' label='Status Changed To' items={status} />
                                </Box>
                            }
                            {(notificationTriggerType == NotificationTriggerType.DaysElapsed) &&
                                <Box>
                                    <Box mt={2}>
                                        <AppSelectList control={control} name='statusToAction' label='Status Unchanged From' items={status} />
                                    </Box>
                                    <Box mt={2}>
                                        <AppTextInput control={control} name="daysElapsed" label={"Number of Days Elapsed"} size={"small"} />
                                    </Box>
                                </Box>
                            }
                            {automatedNotificationType == AutomatedNotificationType.Scorecard_Planner_And_Tracker && notificationTriggerType == NotificationTriggerType.ScheduledDate &&
                                <Box mt={2} width="100%" display="flex" alignItems="center">
                                    <Box flex={1} mr={2}>
                                        <AppTextInput control={control} name="numberOfDaysToSendBeforeScheduleDate" label={"Number of Days Before Schedule Date"} size={"small"} disabled={daysBeforeDisabled} />
                                    </Box>
                                    <Typography variant="body2" component="span">OR</Typography>
                                    <Box flex={1} ml={2}>
                                        <AppTextInput control={control} name="numberOfDaysToSendAfterScheduleDate" label={"Number of Days After Schedule Date"} size={"small"} disabled={daysAfterDisabled} />
                                    </Box>
                                </Box>
                            }
                            <Divider variant="middle" />
                            <Box mt={2}>
                                <AppMultiSelect control={control} name="usersToNotify" label="Users to notify" options={emailUsers} size="small" limit={emailUsers.length} />
                            </Box>
                            <Box mt={2}>
                                <AppTextInput control={control} name="message" label="Message" size={"small"} multiline={true} rows={4} />
                            </Box>
                        </Grid>
                    </Grid>
                }

            </DialogContent>
            <DialogActions>
                <MDButton variant='outlined' color='primary' onClick={() => props.handleClose()}>
                    Cancel
                </MDButton>
                <LoadingButton loading={isSubmitting} variant='contained' form='CompanyDetailsForm' type="submit" color='primary'>
                    Save
                </LoadingButton>
            </DialogActions>
        </Dialog>
    )
}
